import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Draft } from 'immer'
import appAPI from '../../api/api'
import { CommentWindow, CommentWindowRequest, ForumCommentRead } from '../../api/forum-comment'
import { ForumCommentCreate } from '../../api/model/forumCommentCreate'
import { ForumCommentUpdate } from '../../api/model/forumCommentUpdate'

type ForumCommentsState = {
  topicId?: number
  requests: Record<string, { id: string; background: boolean; revision?: number }>
  revision: number
  liveComments: Record<number, { comment: ForumCommentRead | null; revision: number }>
  mutations: Record<string, string>
  loadedPages: Record<number, ForumCommentRead[]>
  window?: Omit<CommentWindow, 'results'>
  error?: string
  syncError?: string
  comments: ForumCommentRead[]
  page: number
  pages: number
  perPage: number
  editingComment?: ForumCommentRead
  repliedToComment?: ForumCommentRead
  loading: boolean
  successSend?: boolean
  createdCommentId?: number
  prevActionIsUpdateComment: boolean
}

export const initialForumCommentsState: ForumCommentsState = {
  comments: [],
  revision: 0,
  liveComments: {},
  requests: {},
  mutations: {},
  loadedPages: {},
  page: 1,
  pages: 1,
  perPage: 20,
  loading: false,
  prevActionIsUpdateComment: false,
}

const fetchForumComments = createAsyncThunk(
  '/fetchForumComments',
  async (payload: GetForumCommentsPayload & { background?: boolean; history?: boolean; historyPage?: number }) => {
    return appAPI.forumComments.getComments(payload)
  },
)

const fetchCommentWindow = createAsyncThunk(
  '/fetchCommentWindow',
  async (payload: CommentWindowRequest, { rejectWithValue }) => {
    try {
      return await appAPI.forumComments.getWindow(payload)
    } catch (error: any) {
      return rejectWithValue({ status: error.response?.status })
    }
  },
)

const createForumComment = createAsyncThunk('/createForumComment', async (payload: Partial<ForumCommentCreate>) => {
  return (await appAPI.forumComments.postComment(payload)).data
})

const updateForumComment = createAsyncThunk(
  '/updateForumComment',
  async (payload: { id: number } & ForumCommentUpdate) => {
    const result = await appAPI.forumComments.putComment(payload)
    return { ...result.data, id: payload.id }
  },
)

const setCommentPinned = createAsyncThunk('/setCommentPinned', async (payload: { id: number; isPinned: boolean }) => {
  return (await appAPI.forumComments.setCommentPinned(payload)).data
})

const removeForumComment = createAsyncThunk('/removeForumComment', async (payload: { id: number }) => {
  await appAPI.forumComments.deleteComment(payload.id)
  return payload.id
})

const updateQuotes = (state: Draft<ForumCommentsState>, id: number, comment?: ForumCommentRead) => {
  const update = (item: Draft<ForumCommentRead>) => {
    if (item.parent?.id !== id) return
    item.parent = comment
      ? { ...item.parent, message: comment.message || '', user: comment.user }
      : { ...item.parent, isDeleted: true, message: 'Сообщение удалено' }
  }
  state.comments.forEach((item) => {
    if (item.parent?.id !== id) return
    update(item)
    state.liveComments[item.id] = { comment: item, revision: state.revision }
  })
  Object.values(state.loadedPages).forEach((items) => items.forEach(update))
}

const applyRead = (state: Draft<ForumCommentsState>, comment: ForumCommentRead, insert = false) => {
  if (state.liveComments[comment.id]?.comment === null) return
  const index = state.comments.findIndex((item) => item.id === comment.id)
  if (insert && state.window?.hasNewer && index === -1) {
    state.revision += 1
    updateQuotes(state, comment.id, comment)
    return
  }
  if (index !== -1 && JSON.stringify(state.comments[index]) === JSON.stringify(comment)) return
  const visible = index !== -1 || insert
  if (index !== -1) state.comments[index] = comment
  else if (insert) state.comments.push(comment)
  let cached = false
  Object.values(state.loadedPages).forEach((items) => {
    const index = items.findIndex((item) => item.id === comment.id)
    if (index !== -1) {
      items[index] = comment
      cached = true
    }
  })
  if (insert && !cached && state.loadedPages[1]) state.loadedPages[1].unshift(comment)
  state.revision += 1
  if (visible) {
    state.liveComments[comment.id] = { comment, revision: state.revision }
    state.comments.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime() || a.id - b.id)
  }
  updateQuotes(state, comment.id, comment)
  if (state.repliedToComment?.id === comment.id) state.repliedToComment = comment
}

const applyDeleted = (state: Draft<ForumCommentsState>, id: number) => {
  state.revision += 1
  state.liveComments[id] = { comment: null, revision: state.revision }
  state.comments = state.comments.filter((comment) => comment.id !== id)
  Object.keys(state.loadedPages).forEach((key) => {
    state.loadedPages[Number(key)] = state.loadedPages[Number(key)].filter((comment) => comment.id !== id)
  })
  updateQuotes(state, id)
  if (state.editingComment?.id === id) state.editingComment = undefined
  if (state.repliedToComment?.id === id) state.repliedToComment = undefined
}

const forumCommentsSlice = createSlice({
  name: 'comments',
  initialState: initialForumCommentsState,
  reducers: {
    beginTopic: (state, { payload }: PayloadAction<number>) => {
      if (state.topicId === payload) return state
      return { ...initialForumCommentsState, topicId: payload }
    },
    showLatest: (state) => {
      state.window = undefined
      state.loading = false
      state.error = undefined
      state.loadedPages = {}
      state.comments = []
      state.requests = {}
      Object.keys(state.liveComments).forEach((key) => {
        if (state.liveComments[Number(key)].comment) delete state.liveComments[Number(key)]
      })
      state.page = 1
    },
    cancelWindowRequest: (state) => {
      delete state.requests.window
      state.loading =
        Object.values(state.requests).some((request) => !request.background) || Object.keys(state.mutations).length > 0
    },
    setSyncError: (state, { payload }: PayloadAction<string | undefined>) => {
      state.syncError = payload
    },
    completeDraft: (state) => {
      state.editingComment = undefined
      state.repliedToComment = undefined
      state.createdCommentId = undefined
    },
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.page = payload
    },
    setRepliedToComment: (state, { payload }: PayloadAction<ForumCommentRead | undefined>) => {
      state.repliedToComment = payload
      state.editingComment = undefined
    },
    setEditingComment: (state, { payload }: PayloadAction<ForumCommentRead | undefined>) => {
      state.editingComment = payload
      state.repliedToComment = undefined
    },
    clearCreatedCommentId: (state) => {
      state.createdCommentId = undefined
    },
    clearComments: () => {
      return initialForumCommentsState
    },
    applyRealtimeCommentCreated: (state, { payload }: PayloadAction<ForumCommentRead>) => {
      applyRead(state, payload, true)
    },
    applyRealtimeCommentUpdated: (state, { payload }: PayloadAction<ForumCommentRead>) => {
      applyRead(state, payload)
    },
    applyRealtimeCommentDeleted: (state, { payload: id }: PayloadAction<number>) => {
      applyDeleted(state, id)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentWindow.pending, (state, { meta }) => {
        if (state.topicId !== meta.arg.topic) return
        if (meta.arg.around !== undefined) state.requests = {}
        state.requests.window = { id: meta.requestId, background: !!meta.arg.background, revision: state.revision }
        state.error = undefined
        state.loading = !meta.arg.background
      })
      .addCase(fetchCommentWindow.fulfilled, (state, { payload, meta }) => {
        if (state.topicId !== meta.arg.topic || state.requests.window?.id !== meta.requestId) return
        const revision = state.requests.window.revision || 0
        delete state.requests.window
        const replacing = meta.arg.around !== undefined
        const data = payload.data
        const rows = new Map<number, ForumCommentRead>()
        if (!replacing) state.comments.forEach((item) => rows.set(item.id, item))
        data.results.forEach((item) => {
          const live = state.liveComments[item.id]
          if (live?.comment === null) return
          let comment = live && live.revision > revision ? live.comment! : item
          const parentComment = comment.parent
          const parent = parentComment && state.liveComments[parentComment.id]
          if (parentComment && parent && (parent.comment === null || parent.revision > revision)) {
            comment = {
              ...comment,
              parent: parent.comment
                ? { ...parentComment, message: parent.comment.message || '', user: parent.comment.user }
                : { ...parentComment, isDeleted: true, message: 'Сообщение удалено' },
            }
          }
          rows.set(item.id, comment)
        })
        if (replacing) {
          const newest = data.results[data.results.length - 1]
          Object.values(state.liveComments).forEach((live) => {
            if (
              !data.hasNewer &&
              live.comment &&
              live.revision > revision &&
              newest &&
              new Date(live.comment.created) >= new Date(newest.created)
            )
              rows.set(live.comment.id, live.comment)
          })
          Object.keys(state.liveComments).forEach((key) => {
            if (state.liveComments[Number(key)].comment && !rows.has(Number(key)))
              delete state.liveComments[Number(key)]
          })
          state.loadedPages = {}
          state.window = {
            olderCursor: data.olderCursor,
            newerCursor: data.newerCursor,
            hasOlder: data.hasOlder,
            hasNewer: data.hasNewer,
          }
        } else if (state.window) {
          if (meta.arg.before) {
            state.window.hasOlder = data.hasOlder
            if (data.olderCursor) state.window.olderCursor = data.olderCursor
          } else {
            state.window.hasNewer = data.hasNewer
            if (data.newerCursor) state.window.newerCursor = data.newerCursor
          }
        }
        state.comments = Array.from(rows.values()).sort(
          (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime() || a.id - b.id,
        )
        state.loading = false
        state.syncError = undefined
      })
      .addCase(fetchCommentWindow.rejected, (state, { meta, payload }) => {
        if (state.topicId !== meta.arg.topic || state.requests.window?.id !== meta.requestId) return
        delete state.requests.window
        state.loading = false
        if (!(meta.arg.around !== undefined && (payload as any)?.status === 404)) {
          state.error = 'Не удалось загрузить сообщения. Повторите попытку.'
        }
      })
      .addCase(fetchForumComments.pending, (state, { meta }) => {
        if (state.topicId !== undefined && state.topicId !== meta.arg.topic) return
        state.requests[String(meta.arg.offset || 0)] = {
          id: meta.requestId,
          background: !!meta.arg.background,
          revision: state.revision,
        }
        state.error = undefined
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
        state.prevActionIsUpdateComment = false
      })
      .addCase(fetchForumComments.fulfilled, (state, { payload, meta }) => {
        const key = String(meta.arg.offset || 0)
        if (state.requests[key]?.id !== meta.requestId) return
        const revision = state.requests[key].revision || 0
        delete state.requests[key]
        state.syncError = undefined
        const page = meta.arg.historyPage || Math.floor((meta.arg.offset || 0) / (meta.arg.limit || state.perPage)) + 1
        state.loadedPages[page] = payload.data.results || []
        const incoming = new Map(state.loadedPages[page].map((item) => [item.id, item]))
        state.loadedPages[page].forEach((item) => {
          const live = state.liveComments[item.id]
          if (live?.comment && live.revision <= revision) delete state.liveComments[item.id]
        })
        Object.keys(state.loadedPages).forEach((key) => {
          state.loadedPages[Number(key)] = state.loadedPages[Number(key)]
            .filter((item) => state.liveComments[item.id]?.comment !== null)
            .map((item) => state.liveComments[item.id]?.comment || incoming.get(item.id) || item)
        })
        if (state.loadedPages[1]) {
          const cached = new Set(Object.values(state.loadedPages).flatMap((items) => items.map((item) => item.id)))
          Object.values(state.liveComments).forEach(({ comment }) => {
            if (comment && !cached.has(comment.id)) state.loadedPages[1].unshift(comment)
          })
        }
        if (meta.arg.history) state.page = Math.max(state.page, page)
        const unique = new Map<number, ForumCommentRead>()
        Object.values(state.loadedPages).forEach((items) => items.forEach((item) => unique.set(item.id, item)))
        Object.keys(state.liveComments).forEach((key) => {
          const live = state.liveComments[Number(key)].comment
          if (live) unique.set(live.id, live)
          else unique.delete(Number(key))
        })
        state.comments = Array.from(unique.values()).sort(
          (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime() || a.id - b.id,
        )
        state.pages = payload.data.pages || 1
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
      })
      .addCase(fetchForumComments.rejected, (state, { meta }) => {
        const key = String(meta.arg.offset || 0)
        if (state.requests[key]?.id !== meta.requestId) return
        delete state.requests[key]
        state.error = 'Не удалось загрузить сообщения. Повторите попытку.'
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
      })

      .addCase(createForumComment.pending, (state, { meta }) => {
        state.mutations['createForumComment'] = meta.requestId
        state.error = undefined
        state.loading = true
        state.createdCommentId = undefined
        state.successSend = false
        state.prevActionIsUpdateComment = false
      })
      .addCase(createForumComment.fulfilled, (state, { payload, meta }) => {
        if (state.mutations.createForumComment !== meta.requestId) return
        delete state.mutations.createForumComment
        state.loading = false
        if (!state.loadedPages[1]) state.page = 1
        applyRead(state, payload, true)
        state.createdCommentId = payload.id
        state.successSend = true
      })
      .addCase(createForumComment.rejected, (state, { meta }) => {
        if (state.mutations['createForumComment'] !== meta.requestId) return
        delete state.mutations['createForumComment']
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
        state.successSend = false
        state.error = 'Не удалось сохранить изменения. Повторите попытку.'
      })

      .addCase(updateForumComment.pending, (state, { meta }) => {
        state.mutations['updateForumComment'] = meta.requestId
        state.error = undefined
        state.loading = true
        state.createdCommentId = undefined
        state.successSend = false
        state.prevActionIsUpdateComment = true
      })
      .addCase(updateForumComment.fulfilled, (state, { payload, meta }) => {
        if (state.mutations.updateForumComment !== meta.requestId) return
        delete state.mutations.updateForumComment
        applyRead(state, { ...state.comments.find((comment) => comment.id === payload.id), ...payload })
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
        state.createdCommentId = payload.id
        state.successSend = true
      })
      .addCase(updateForumComment.rejected, (state, { meta }) => {
        if (state.mutations['updateForumComment'] !== meta.requestId) return
        delete state.mutations['updateForumComment']
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
        state.successSend = false
        state.error = 'Не удалось сохранить изменения. Повторите попытку.'
      })

      .addCase(setCommentPinned.fulfilled, (state, { payload }) => {
        applyRead(state, payload)
      })

      .addCase(removeForumComment.pending, (state, { meta }) => {
        state.mutations['removeForumComment'] = meta.requestId
        state.error = undefined
        state.loading = true
        state.prevActionIsUpdateComment = true
      })
      .addCase(removeForumComment.fulfilled, (state, { payload: id, meta }) => {
        if (state.mutations.removeForumComment !== meta.requestId) return
        delete state.mutations.removeForumComment
        applyDeleted(state, id)
        state.loading = false
      })
      .addCase(removeForumComment.rejected, (state, { meta }) => {
        if (state.mutations['removeForumComment'] !== meta.requestId) return
        delete state.mutations['removeForumComment']
        state.loading =
          Object.values(state.requests).some((request) => !request.background) ||
          Object.keys(state.mutations).length > 0
        state.successSend = false
        state.error = 'Не удалось сохранить изменения. Повторите попытку.'
      })
  },
})

export default forumCommentsSlice.reducer

export const forumCommentsActions = {
  ...forumCommentsSlice.actions,
  fetchForumComments,
  fetchCommentWindow,
  createForumComment,
  updateForumComment,
  setCommentPinned,
  removeForumComment,
}
